import React, { Component, createRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { throttle } from "lodash";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import { Grid, Header, Message, Ref, Sticky } from "semantic-ui-react";
import cx from "classnames";
import { StatusBar } from "@uppy/react";
import "@uppy/core/dist/style.css";
import "@uppy/status-bar/dist/style.css";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import Tour from "~/models/Tour";
import MediaItemStore from "~/stores/Media/MediaItemStore";
import MediaList from "./MediaEdit/MediaList";
import ExpandedForm from "./MediaEdit/ExpandedForm";
import InlineForm from "./MediaEdit/InlineForm";
import DragAndDropUploader from "./MediaEdit/DragAndDropUploader";
import MediaEditHeader from "./MediaEdit/MediaEditHeader";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import { attachAutoScrollHandler, detachAutoScrollHandler } from "~/utils/DnDAutoScroll";
import "../styles/MediaEdit.less";
import SaveButton from "../components/SaveButton";
import StickyMenu from "~/components/StickyMenu";
import ToastMessageStore from "~/stores/ToastMessageStore";

const validationSchema = {
  name: {
    required: true,
    maxLength: 100,
  },
  description: {
    maxLength: 500,
  },
  duration: {
    isInt: true,
  },
};

@observer
class MediaEdit extends Component {
  /** @type MediaItemStore */
  mediaItemStore = null;

  inlineFormRef = null;

  constructor(props) {
    super(props);
    document.title = "Tour - Media Edit";
    this.mediaItemStore = new MediaItemStore();

    this.inlineFormRef = createRef();

    const { history, location } = props;
    history.listen(historyLocation => {
      this.mediaItemStore.inboxPulling = historyLocation.pathname === location.pathname;
    });

    this.fixStickyInlineFormOffset = this.fixStickyInlineFormOffset.bind(this);
    this.fixStickyInlineFormOffset = throttle(this.fixStickyInlineFormOffset, 100, { leading: true, trailing: true });

    this.state = {
      showExpandedForm: false,
      showInlineForm: false,
      sticky: false,
    };
  }

  componentDidMount() {
    const { tourId } = this.props;

    this.mediaItemStore.load(tourId);
    this.mediaItemStore.configureUploader(tourId);
    this.mediaItemStore.checkTourInbox(tourId);

    attachAutoScrollHandler();
    this.mediaItemStore.attachEventListeners();
    window.scrollTo(0, 0);

    document.addEventListener("scroll", this.fixStickyInlineFormOffset);
  }

  componentWillUnmount() {
    detachAutoScrollHandler();
    this.mediaItemStore.inboxPulling = false;
    if (this.mediaItemStore.inboxTimerId) {
      clearTimeout(this.mediaItemStore.inboxTimerId);
      this.mediaItemStore.inboxTimerId = null;
    }
    this.mediaItemStore.detachEventListeners();
    document.removeEventListener("scroll", this.fixStickyInlineFormOffset);
  }

  onFileReady(file, blob) {
    // Uppy will look at 'data' property to access the file blob.
    // eslint-disable-next-line no-param-reassign
    file.data = blob;
    this.mediaItemStore.addFile(file);
  }

  /**
   * Saves form data, but also sets a media item as selected. Fixes NEST-1177.
   */
  onSubmit(closeFormOnSuccess = false) {
    const { t, showError, toastMessageStore } = this.props;
    const formStore = this.mediaItemStore.getFormStore();
    const { model, isDirty } = formStore;

    const validation = getValidationErrors(validationSchema, model);

    // take validation into account only if the model is dirty.
    if (isDirty && !validation.isValid) {
      // break if form is invalid.
      return Promise.resolve();
    }

    return this.mediaItemStore
      .save()
      .then(() => {
        if (closeFormOnSuccess) {
          this.closeForm(model.mediaItemId);
        }

        toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
      })
      .catch(error => {
        showError(error);
        this.mediaItemStore.isLoading = false;

        throw error;
      });
  }

  /**
   * Does not trigger a mediaItem selection, only saves data.
   */
  onSequenceSave() {
    const { showError } = this.props;

    return this.mediaItemStore.save().catch(error => {
      showError(error);
      this.mediaItemStore.isLoading = false;

      throw error;
    });
  }

  onMediaItemClick(dragItem, mouseEvent) {
    this.mediaItemStore.onClick(dragItem.id, mouseEvent);
    this.mediaItemStore.bindMediaItemToForm(dragItem.id);
    this.setState({ showInlineForm: true });
  }

  fixStickyInlineFormOffset() {
    // This code assumes that the Sticky header's height is around 77px, this is not likely to change
    // so it's not worth to compute it on scroll since it's cpu intensive.
    const stickyHeaderHeight = 77;
    // The intention is to add an offset to the sticky form but only once it intersects wit the sticky header.
    // this threshold will give us some padding between the sticky components.
    const topThreshold = 50;

    const elements = document.getElementsByClassName("media-list-column");

    if (elements.length === 0) {
      return;
    }

    const rects = elements[0].getClientRects();

    if (!rects || typeof rects[0] === "undefined") {
      return;
    }

    const stickyInlineForm = rects[0];

    if (stickyInlineForm.top < stickyHeaderHeight + topThreshold) {
      this.setState({ sticky: true });
    } else {
      this.setState({ sticky: false });
    }
  }

  showExpandedForm(mediaItemId) {
    this.mediaItemStore.bindMediaItemToForm(mediaItemId);
    this.setState({ showExpandedForm: true });
  }

  closeForm(mediaItemId) {
    this.mediaItemStore.resetForm();
    // once the form is saved or cancelled, highlight the media item that we have just edited, so the user
    // can track which media item they were editing (since they are taken back to the media list view).

    // This condition is needed because it could happen that we are only saving when the sequence has change,
    // and we clicked the sticky "save" button. In that context, there's no mediaId available.
    if (mediaItemId) {
      this.mediaItemStore.setSelected(mediaItemId, true);
    }

    // hide both form types.
    this.setState({ showExpandedForm: false, showInlineForm: false });
  }

  stickySaveButtonEnabled(validation) {
    const { hasSequencesChanged, inlineFormStoresDirty } = this.mediaItemStore;
    const formStore = this.mediaItemStore.getFormStore();

    // if there are form changes...
    if (formStore.isDirty) {
      // enable sticky save button as long as the form is valid.
      return validation.isValid;
    }

    // if form is pristine, then enable the button if there are sequence changes or inline forms are dirty
    return hasSequencesChanged || inlineFormStoresDirty;
  }

  render() {
    const { tourId, tour, t, tourLayoutContentContainerRef, toastMessageStore } = this.props;
    const { showExpandedForm, showInlineForm, sticky } = this.state;
    const {
      mediaItems,
      inlineFormStores,
      inlineFormStoresDirty,
      hasSequencesChanged,
      uploader,
      multipartUploader,
      tourInbox,
      isSaving,
      isLoading,
    } = this.mediaItemStore;
    const formStore = this.mediaItemStore.getFormStore();
    const { model, isDirty, bindInput, setProperty } = formStore;
    const hasUnsavedChanges = isDirty || hasSequencesChanged || inlineFormStoresDirty;
    const uploadEnabled = tour && tour.clientPanelOptions.enableClientPanelMediaUpload;

    if (isLoading) {
      return <ContentLoader />;
    }

    const validation = getValidationErrors(validationSchema, model);

    return (
      <>
        <ModalUnsavedConfirmation
          when={hasUnsavedChanges}
          onSave={() => this.mediaItemStore.save().then(() => toastMessageStore.addChangesSavedToastMessage())}
        />
        <LoadingWrapper loading={isSaving} message={t("saving")} />

        <StickyMenu
          contextRef={tourLayoutContentContainerRef}
          headerText={t("clientPanelMisc:mediaEdit")}
          subHeader={
            <Header.Subheader>
              <Link to={`/tours/${tourId}/download`}>{t("clientPanelMisc:downloadMedia")}</Link>
            </Header.Subheader>
          }
          button={
            <div className="responsive-save-buttons">
              {/* Difference between them is that the mobile version will pass "closeFormOnSuccess" = true */}
              <SaveButton
                className="computer"
                disabled={!this.stickySaveButtonEnabled(validation)}
                onClick={() => this.onSubmit()}
              />
              <SaveButton
                className="mobile"
                disabled={!this.stickySaveButtonEnabled(validation)}
                onClick={() => this.onSubmit(true)}
              />
            </div>
          }
        />
        <Message className="media-edit-message-container">
          <Message.List>
            <Message.Item>
              {t("clientPanelMisc:mediaDownloadsMovedTo")}{" "}
              <Link to={`/tours/${tourId}/download#video-download`}>
                <u>{t("clientPanelMisc:downloadCenter")}</u>
              </Link>
              . {t("clientPanelMisc:downloadLinksAppearUnderTitle")}
            </Message.Item>
            <Message.Item>{t("clientPanelMisc:dragDropMediaReorderEdit")}</Message.Item>
          </Message.List>
        </Message>
        <Grid
          className={cx("media-edit", { "show-expanded-form": showExpandedForm, "show-inline-form": showInlineForm })}
        >
          <Grid.Row className="expanded-form-container">
            <Grid.Column>
              {model.mediaItemId && (
                <ExpandedForm
                  model={model}
                  validation={validation}
                  serverValidation={this.mediaItemStore.errorMessages}
                  bindInput={bindInput}
                  setProperty={setProperty}
                  // bind the model.id which points to a media id, so we can highlight the media item that was in
                  // edit view
                  onCancel={() => this.closeForm(model.mediaItemId)}
                  onSubmit={() => this.onSubmit(true)}
                />
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="media-edit-outer-row">
            <Grid.Column>
              <MediaEditHeader
                tourInbox={tourInbox}
                onFileReady={(file, blob) => this.onFileReady(file, blob)}
                uploadEnabled={uploadEnabled}
                selectedMediaItems={mediaItems.filter(m => m.isSelected)}
                onIncludeInChange={includeIn => this.mediaItemStore.saveSelectedMediaItemsIncludeIn(includeIn)}
              />
              {uploader && (
                <StatusBar uppy={uploader} hideUploadButton hideAfterFinish showProgressDetails width="100%" />
              )}
              {multipartUploader && (
                <StatusBar uppy={multipartUploader} hideUploadButton hideAfterFinish showProgressDetails width="100%" />
              )}
              <Ref innerRef={this.inlineFormRef}>
                <Grid>
                  <Grid.Column>
                    <div className="media-list-column">
                      <div className="media-list-container">
                        <DragAndDropUploader
                          disabled={!uploadEnabled}
                          onFileReady={(file, blob) => this.onFileReady(file, blob)}
                        >
                          <MediaList
                            mediaItems={mediaItems}
                            inlineFormStores={inlineFormStores}
                            draggingItemId={this.mediaItemStore.dndStore.draggingItemId}
                            onEdit={mediaItem => this.showExpandedForm(mediaItem.mediaItemId)}
                            reorder={(sourceItem, targetItem) =>
                              this.mediaItemStore.reorder(sourceItem.id, targetItem.id)
                            }
                            onItemDragEnd={(sourceItem, targetItem) =>
                              this.mediaItemStore.onItemDragEnd(sourceItem, targetItem)
                            }
                            onItemDragBegin={item => this.mediaItemStore.onItemDragBegin(item.id)}
                            onClick={(item, mouseEvent) => this.onMediaItemClick(item, mouseEvent)}
                            onCancelEdit={mediaItemId => this.mediaItemStore.cancelEdit(mediaItemId)}
                            onSubmit={() => this.onSubmit()}
                          />
                        </DragAndDropUploader>
                      </div>

                      <div className="inline-form-container">
                        {/* When  the main sticky header + button goes int "sticky" mode, we need
                        to fix this other Sticky form, otherwise they will overlap. The value 85 is roughly
                        the height of the first Sticky component. */}
                        <Sticky context={this.inlineFormRef} offset={sticky ? 90 : 0}>
                          {model.mediaItemId && (
                            <InlineForm
                              model={model}
                              validation={validation}
                              serverValidation={this.mediaItemStore.errorMessages}
                              bindInput={bindInput}
                              setProperty={setProperty}
                              // bind the model.id which points to a media id, so we can highlight the media item that was in
                              // edit view
                              onCancel={() => this.closeForm(model.mediaItemId)}
                              onSubmit={() => this.onSubmit()}
                            />
                          )}
                        </Sticky>
                      </div>
                    </div>
                  </Grid.Column>
                </Grid>
              </Ref>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </>
    );
  }
}

MediaEdit.propTypes = {
  tourId: PropTypes.number.isRequired,
  tour: PropTypes.instanceOf(Tour),
  history: PropTypes.object,
  location: PropTypes.object,
  showError: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(TourLayoutWrapper(MediaEdit, TourNav.MEDIA_EDIT));
